import React, { useState, useRef } from 'react';
import Button3D from '../pages/Button3D';
import { login, signup, forgotPassword } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function AuthModal({ showAuthModal, setShowAuthModal }) {
  const [authMode, setAuthMode] = useState("signin"); // "signin" or "signup"
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Always store normalized accountType (lowercase, underscore)
  const [accountType, setAccountType] = useState("");
  const [authError, setAuthError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const emailRef    = useRef(null);
  const passwordRef = useRef(null);

  const { login: authLogin } = useAuth();

  if (!showAuthModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError("");
    setIsLoading(true);

    // Read DOM values to catch browser/password-manager autofill that bypasses onChange
    const effectiveEmail    = authEmail    || emailRef.current?.value    || '';
    const effectivePassword = authPassword || passwordRef.current?.value || '';

    // Sync state so the login call uses the autofilled values
    if (!authEmail    && effectiveEmail)    setAuthEmail(effectiveEmail);
    if (!authPassword && effectivePassword) setAuthPassword(effectivePassword);

    // Basic validation
    if (!effectiveEmail || !effectivePassword) {
      setAuthError("Please fill in all fields");
      setIsLoading(false);
      return;
    }
    
    if (authMode === "signup" && !authName) {
      setAuthError("Please enter your name");
      setIsLoading(false);
      return;
    }
    
    if (authMode === "signup" && !accountType) {
      setAuthError("Please select an account type");
      setIsLoading(false);
      return;
    }
    
    try {
      if (authMode === "signin") {
        console.log('📧 Attempting login with:', { email: effectiveEmail, password: '***' });

        // Handle login
        const result = await login({
          email: effectiveEmail,
          password: effectivePassword
        });
        
        console.log('🔄 Login result received:', result);
        
        if (result.success) {
          console.log('✅ Login successful, calling authLogin with:', result.user);
          
          // Store user data and token in context/localStorage, including trial status
          authLogin(result.user, result.token, result.trial_status);
          
          // Show success message in green
          setAuthError(`✅ Login successful! Welcome ${result.user.name}`);
          
          // Only close modal if trial is not expired (authLogin will handle expired trial)
          if (!result.trial_status?.expired) {
            setTimeout(() => {
              setShowAuthModal(false);
              setAuthEmail("");
              setAuthPassword("");
              setAuthError("");
            }, 1500);
          } else {
            // Close modal immediately for expired trials - the modal will be shown by AuthContext
            setShowAuthModal(false);
            setAuthEmail("");
            setAuthPassword("");
            setAuthError("");
          }
          
          console.log('🎉 Login process completed successfully');
        } else {
          console.log('❌ Login failed:', result.message);
          setAuthError(result.message || "Login failed. Please check your credentials.");
        }
      } else {
        // Handle signup
        const result = await signup({
          name: authName,
          email: effectiveEmail,
          password: effectivePassword,
          accountType: accountType
        });
        
        if (result.success) {
          // Show success message and switch to login
          setAuthError("");
          setAuthMode("signin");
          
          // Clear signup fields
          setAuthName("");
          setAuthEmail("");
          setAuthPassword("");
          setAccountType("");
          
          // Show success message
          setAuthError("Account created successfully! Please sign in.");
        } else {
          setAuthError(result.message || "Signup failed. Please try again.");
        }
      }
    } catch (err) {
      setAuthError("An error occurred. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError("");
    setIsLoading(true);
    
    if (!resetEmail) {
      setAuthError("Please enter your email address");
      setIsLoading(false);
      return;
    }
    
    try {
      const result = await forgotPassword(resetEmail);
      
      if (result.success) {
        setResetSent(true);
        setAuthError("");
      } else {
        setAuthError(result.message || "Failed to send reset email. Please try again.");
      }
    } catch (err) {
      setAuthError("An error occurred. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1001
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '30px',
        width: '400px',
        maxWidth: '90%',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#282c34da',
          color: 'white',
          padding: '15px 20px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          margin: '-30px -30px 20px -30px',
          position: 'relative'
        }}>
          <h2 style={{ margin: 0 }}>
            {showForgotPassword ? "Reset Password" : 
             authMode === "signin" ? "Sign In" : "Sign Up"}
          </h2>
          <button
            type="button"
            className="btn-close"
            style={{ filter: 'invert(1)' }}
            onClick={() => {
              setShowAuthModal(false);
              setShowForgotPassword(false);
              setResetSent(false);
              setAuthError("");
              setAuthName("");
              setAuthEmail("");
              setAuthPassword("");
              setAccountType("");
            }}
          />
        </div>
        
        {authError && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: (authError.includes("successful") || authError.includes("✅")) ? '#e8f5e9' : '#ffebee', 
            color: (authError.includes("successful") || authError.includes("✅")) ? '#4caf50' : '#f44336',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            {authError}
          </div>
        )}
        
        {showForgotPassword ? (
          resetSent ? (
            <div>
              <p style={{ color: '#4caf50' }}>
                ✅ Password reset link sent! Please check your email.
              </p>
              <Button3D
                style={{ width: '100%', marginTop: '15px' }}
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetSent(false);
                  setResetEmail("");
                }}
              >
                Back to Sign In
              </Button3D>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <Button3D
                style={{ width: '100%', marginTop: '15px' }}
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button3D>
              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none', padding: 0, color: '#1976d2', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit}>
            {authMode === "signup" && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={authName}
                    onChange={(e) => setAuthName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Account Type
                  </label>
                  <select
                    value={accountType}
                    onChange={(e) => {
                      // Always normalize to lowercase, underscores
                      const val = e.target.value.toLowerCase().replace(/ /g, '_');
                      setAccountType(val);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '16px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select Account Type</option>
                    <option value="solo">Individual - Solo practitioner</option>
                    <option value="solo_assistant">Solo + Assistant - Solo with admin support</option>
                    <option value="team">Team - Organization with multiple members</option>
                    <option value="broker">Broker - Full administrative access</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    {accountType === "solo" && "Perfect for individual real estate professionals managing their own calendar."}
                    {accountType === "solo_assistant" && "Ideal for solo agents with assistant support for calendar management."}
                    {accountType === "team" && "Best for teams and organizations with multiple members and shared calendars."}
                    {accountType === "broker" && "For brokers and administrators who need full access to all connection types and management features."}
                  </small>
                </div>
              </>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Email Address
              </label>
              <input
                ref={emailRef}
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    paddingRight: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666'
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>
            
            {authMode === "signin" && (
              <div style={{ marginBottom: '15px', textAlign: 'right' }}>
                <button
                  type="button"
                  style={{ background: 'transparent', border: 'none', padding: 0, color: '#1976d2', fontSize: '14px', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot password?
                </button>
              </div>
            )}
            
            <Button3D
              style={{ width: '100%', marginTop: '15px' }}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading 
                ? (authMode === "signin" ? "Signing In..." : "Signing Up...") 
                : (authMode === "signin" ? "Sign In" : "Sign Up")}
            </Button3D>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              {authMode === "signin" ? (
                <p>
                  Don't have an account?{' '}
                  <a 
                    href="/signup" 
                    style={{ textDecoration: 'none', color: '#1976d2', fontWeight: '500' }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAuthModal(false);
                      window.location.href = '/signup';
                    }}
                  >
                    Sign Up
                  </a>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    style={{ background: 'transparent', border: 'none', padding: 0, color: '#1976d2', fontWeight: '500', cursor: 'pointer', font: 'inherit' }}
                    onClick={() => { setAuthMode("signin"); setAuthError(""); setAccountType(""); }}
                  >
                    Sign In
                  </button>
                </p>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default AuthModal;
